// Common models across the plugin client and plugin manager. Every item
// exported by this module is a direct or indirect import of both the client
// and manager projects.
import * as t from 'io-ts';
import { CONNECTION_SCHEMES } from "./constants";
import { createCodec, isSerializable, NonEmptyString } from "./modelUtils";
export var SerializableRecord = createCodec('SerializableRecord', function (r) { return t.UnknownRecord.is(r) && isSerializable(r); });
export var SerializableArray = createCodec('SerializableArray', function (r) { return t.UnknownArray.is(r) && isSerializable(r); });
/**
 * A FormData object (for multipart/form-data requests)
 */
export var FormDataCodec = createCodec('FormData', function (r) { return r instanceof FormData; });
export var MessageData = createCodec('MessageData', function (m) {
    return isSerializable(m);
});
export var BaseHandler = createCodec('handler', function (h) {
    return t.Function.is(h);
});
/**
 * Fields that are required for all fetch connection specifiers.
 */
var CommonConnectionFields = t.type({
    connectionName: NonEmptyString,
});
/**
 * Fields that are required for egress connections, but not for internal
 * connections.
 */
export var EgressConnectionFields = t.type({
    paramFormat: NonEmptyString,
    paramName: NonEmptyString,
    paramTemplate: NonEmptyString,
});
/**
 * An object that specifies a connection to use. Note that it's not known in
 * the plugin client whether the connection is internal or not, so several
 * fields here are marked optional. They're enforced in the manager, after the
 * connection name is resolved against the list of the extension's connections.
 */
var FetchConnectionSpecifier = t.intersection([CommonConnectionFields, t.partial(EgressConnectionFields.props)], 'Connection Configuration For Request');
/**
 * Egress gateway credential provided by user making egress request
 */
export var EgressFetchConnectionSpecifier = t.intersection([CommonConnectionFields, EgressConnectionFields], 'Credential Configuration For Egress Request');
/**
 * Fetch config provided by developer
 */
export var UserProvidedFetchConfig = t.intersection([
    t.type({
        // required parameters
        method: NonEmptyString,
    }),
    t.partial({
        // optional parameters
        headers: t.record(t.string, t.string),
        body: t.union([SerializableRecord, SerializableArray, t.string, FormDataCodec]),
        params: t.record(t.string, t.string),
        connection: FetchConnectionSpecifier,
    }),
]);
/**
 * Invoke predefined request parameters provided by developer
 */
export var UserProvidedPredefinedRequest = t.intersection([
    t.type({
        // required parameters
        requestName: NonEmptyString,
        connectionName: NonEmptyString,
    }),
    t.partial({
        // optional parameters
        parameters: t.record(t.string, t.string),
    }),
]);
/**
 * fetchQualtricsApi config provided by Developer
 */
export var UserProvidedQualtricsApiFetchConfig = t.intersection([
    t.type({
        method: NonEmptyString,
    }),
    t.partial({
        headers: t.record(t.string, t.string),
        body: MessageData,
        params: t.record(t.string, t.string),
    }),
]);
/**
 * Valid connection config
 */
export function isValidSimpleConnectionScheme(s) {
    return (typeof s == 'string' && Object.values(CONNECTION_SCHEMES).includes(s));
}
var SimpleConnectionScheme = createCodec('scheme', function (s) {
    return isValidSimpleConnectionScheme(s);
});
export function isContext(c) {
    if (typeof c !== 'object')
        return false;
    var co = c;
    if (!co)
        return false;
    if (!co.instanceID)
        return false;
    if (typeof co.instanceID !== 'string')
        return false;
    if (!co.language)
        return false;
    if (typeof co.language !== 'string')
        return false;
    if (!co.availableConnections)
        return false;
    if (!Array.isArray(co.availableConnections))
        return false;
    if (co.connections) {
        if (!(co.connections instanceof Array))
            return false;
        return co.connections.every(function (c) { return c.name && c.scheme; });
    }
    return true;
}
export function isTranslationFile(t) {
    if (!t.strings)
        return false;
    if (typeof t.strings !== 'object')
        return false;
    return true;
}
export var EgressResponse = t.intersection([
    t.type({
        httpStatusCode: t.number,
        responseBody: t.string,
    }),
    t.partial({
        responseHeaders: t.record(t.string, t.string),
    }),
]);
// InitMessage contains information sent by the plugin client on
// initialization. For backward compatibility with earlier versions of the
// plugin client, which didn't send any contents at all, this can be undefined,
// and all fields are optional.
export var InitMessage = t.union([
    t.undefined,
    t.partial({
        // optional parameters
        clientVersion: t.string,
    }),
]);
export var CredentialStatus;
(function (CredentialStatus) {
    CredentialStatus["INITIALIZED"] = "initialized";
    CredentialStatus["NEW"] = "new";
    CredentialStatus["DELETED"] = "deleted";
    CredentialStatus["UPDATED"] = "updated";
})(CredentialStatus || (CredentialStatus = {}));
export function isExtensionConfigurationValue(value) {
    // Check for primitive types
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return true;
    }
    // Check for object (including arrays since arrays are objects in JS)
    if (value !== null && typeof value === 'object') {
        return true;
    }
    return false;
}
//# sourceMappingURL=models.js.map